import { mapState } from 'vuex';
import { apiCallers } from '@/api/apiRequest';
import { downloadBase64 } from '@/utils/download';
import { formatMoney, randomStr, formatPercent } from '@/modules/utils';
import chart from './chart';
import nNumber from '@/components/common/n-number.vue';
import rowInput from './row-input';
import scrollHandler from '@/mixins/scrollHandler.js';
import themeHandler from '@/mixins/themeHandler.js';
import trackingHandler from '@/mixins/trackingHandler.js';

const POLL_INTERVAL = 500;

export default {
  components: {
    chart,
    nNumber,
    rowInput
  },

  mixins: [
    scrollHandler,
    themeHandler,
    trackingHandler
  ],

  data() {
    return {
      /* global __CURRENT_BUILDTARGET */
      buildTarget: __CURRENT_BUILDTARGET,
      rr_config: {
        hoechstbetrag_single: 0,
        hoechstbetrag_spouses: 0,
        max_foerder_single: 0,
        max_foerder_spouses: 0,
        Year: 0
      },
      checkValid: false,
      form: {
        marital: 0,
        birth_year: '',
        child_num: -1,
        child_birth1: '',
        child_birth2: '',
        child_birth3: '',
        child_birth4: '',
        gross_salary: '',
        retire_age: 0
      },
      optChildren: [
        { label: 'Bitte wählen', value: -1 },
        { label: 'Keine Kinder', value: 0 },
        { label: '1 Kind', value: 1 },
        { label: '2 Kinder', value: 2 },
        { label: '3 Kinder', value: 3 },
        { label: '4 Kinder', value: 4 },
      ],
      optMarital: [
        { label: 'Bitte wählen', value: 0 },
        { label: 'Single', value: 1 },
        { label: 'Ehe-/Lebenspartner', value: 2 },
      ],

      pdfJobId: null,
      pdfPollTimer: null,
      result: null,

      fieldNames: {
        marital: 'familienstand',
        birth_year: 'geburtsjahr',
        child_num: 'anzahl_kinder',
        child_birth1: 'geburtsjahr_k1',
        child_birth2: 'geburtsjahr_k2',
        child_birth3: 'geburtsjahr_k3',
        child_birth4: 'geburtsjahr_k4',
        gross_salary: 'jahresbruttogehalt',
        retire_age: 'rentenbeginn'
      }
    };
  },

  computed: {
    ...mapState([
      'appConfigs'
    ]),

    apiRequest () {
      return apiCallers[this.config.app];
    },

    config () {
      return this.appConfigs[this.$options.configIndex] || {};
    },

    chartItems() {
      return [
        {
          name: 'Grundzulage',
          field: 'basic_allowance',
          color: this.config.theme === 'vkb' ? '#94AE47' : this.config.theme === 'slv' ? '#024589' : '#DE2920',
          selected: false
        },
        {
          name: 'Kinderzulage',
          field: 'child_allowance',
          color: this.config.theme === 'vkb' ? '#14885E' : '#666666',
          selected: false
        },
        {
          name: 'Steuerersparnis',
          field: 'tax_saving',
          color: this.form.child_num >= 1
            ? this.config.theme === 'vkb' ? '#94AE47' : this.config.theme === 'slv' ? '#024589' : '#DE2920'
            : this.config.theme === 'vkb' ? '#14885E' : '#666666',
          selected: false
        },
        {
          name: 'Eigenbeitrag',
          field: 'yearly_self_contribution',
          color: this.config.theme === 'vkb' ? '#E4ECF8' : '#E4ECF8',
          selected: false
        },
      ];
    },

    currentYear() {
      return this.rr_config && this.rr_config.Year
        ? this.rr_config.Year
        : new Date().getFullYear();
    },

    minChildYear() {
      return this.currentYear - 25;
    },

    minBirthYear() {
      console.log('current year:', this.currentYear);
      console.log('min year:', this.currentYear - 66);
      return this.currentYear - 66;
    },

    maxBirthYear() {
      return this.currentYear - 14;
    },

    optRetireAges() {
      let options = [
        { label: 'Bitte wählen', value: 0 },
        { label: '62 Jahre', value: 62 },
        { label: '63 Jahre', value: 63 },
        { label: '64 Jahre', value: 64 },
        { label: '65 Jahre', value: 65 },
        { label: '66 Jahre', value: 66 },
        { label: '67 Jahre', value: 67 },
      ];

      let age = this.currentYear - this.form.birth_year;

      return options
        .filter(option => (
          !option.value || option.value >= age + 1
        ));
    },

    chartData() {
      let _data = {};
      if (this.form.child_num >= 1) {
        _data = this.chartItems
          .map(item => {
            item.value = this.result[item.field];
            return item;
          });
      } else {
        _data = this.chartItems
          .filter((_, i) => i !== 1)
          .map((item) => {
            item.value = this.result[item.field];
            return item;
          });
      }
      return _data;
    },

    resultData() {
      if (!this.result) {
        return [];
      }

      if (this.form.child_num > 0) {
        return [
          {
            label: `Mein Eigenbeitrag in ${ this.currentYear }`,
            value: `${ this.formatMoney(this.result.yearly_self_contribution, 2) } €`
          },
          {
            label: `Förderquote in ${ this.currentYear }`,
            value: `${ this.formatMoney(this.result.yearly_funding_rate, 2) } %`
          },
          {
            label: `<b>Staatliche Förderung in ${ this.currentYear }</b>\nsetzt sich zusammen aus`,
            value: `${ this.formatMoney(this.result.yearly_state_funding, 2) } €`
          },
          {
            label: 'Grundzulage',
            value: `${ this.formatMoney(this.result.basic_allowance, 2) } €`
          },
          {
            label: 'Kinderzulage',
            value: `${ this.formatMoney(this.result.child_allowance, 2) } €`
          },
          {
            label: 'Steuerersparnis',
            value: `${ this.formatMoney(this.result.tax_saving, 2) } €`
          }
        ];
      } else {
        return [
          {
            label: `Mein Eigenbeitrag in ${ this.currentYear }`,
            value: `${ this.formatMoney(this.result.yearly_self_contribution, 2) } €`
          },
          {
            label: `Förderquote in ${ this.currentYear }`,
            value: `${ this.formatMoney(this.result.yearly_funding_rate, 2) } %`
          },
          {
            label: `<b>Staatliche Förderung in ${ this.currentYear }</b>\nsetzt sich zusammen aus`,
            value: `${ this.formatMoney(this.result.yearly_state_funding, 2) } €`
          },
          {
            label: 'Grundzulage',
            value: `${ this.formatMoney(this.result.basic_allowance, 2) } €`
          },
          {
            label: 'Steuerersparnis',
            value: `${ this.formatMoney(this.result.tax_saving, 2) } €`
          }
        ];
      }
    },

    validIncomeValues() {
      return [];
    },

    text() {
      return {
        intro_main: 'Mit dem Älter werden beschäftigt sich keiner gerne. Schon gar nicht mit dem Ausrechnen des späteren Ruhestands. Wir machen es Ihnen leicht: mit unserem Riester-Rechner. Rechnen Sie sich online schon mal aus, wieviel Zulagen Sie erhalten, wieviel Steuern Sie dabei unterm Strich sparen können und wie hoch Ihr Eigenanteil dabei ist. Probieren Sie es aus, es geht schnell und einfach!',
        intro_sub:  'Jeder Mensch und jede Lebenssituation sind anders: Ob Sie Kinder haben oder nicht, wieviel Sie verdienen, wann Sie in Rente gehen wollen – all das muss für die Höhe der Riester-Rente, den staatlichen Zulagen und der möglichen Steuerersparnis berücksichtigt werden. Unser Riester- Rechner erledigt das mit wenigen Eingaben. Doch so gut unser Online-Rechner auch ist – am besten besprechen Sie das Ergebnis persönlich mit einem unserer erfahrenen Berater.',
        footer: [
          'Diese Berechnung ist unverbindlich und erhebt keinen Anspruch auf Vollständigkeit.',
          'Die Höhe der möglichen staatlichen Förderung basiert auf dem aktuellen Kenntnisstand und den gültigen Gesetzen mit Stand 1.1.' + this.currentYear + '. Die angegebene Förderquote entspricht dabei der durchschnittlichen Förderquote über die gesamte Aufschubdauer des Vertrags bis zum geplanten Rentenbeginn.',
          'Die Steuerersparnis stellt lediglich eine Modellrechnung unter Berücksichtigung der individuellen Eingaben dar, da bei der Berechnung unter anderem vom voraussichtlichen Einkommen ausgegangen wird.',
          'Die angegebenen Berechnungen basieren auch auf den Annahmen, dass ggf. Kinderzulagen bis zum 25. Lebensjahr eingerechnet werden und ein ggf. bestehender Riestervertrag des Ehe- bzw. Lebenspartners nicht berücksichtigt wird.',
          'Aufgrund der Modellhaftigkeit unserer Berechnungen kann es im Verhältnis zur tatsächlichen Steuerberechnung zu Abweichungen kommen. Die Berechnung der tatsächlichen Steuerersparnis kann nur unter Zugrundelegung aller Gesamtumstände des individuellen Einzelfalls ermittelt werden und ersetzt nicht die Hinzuziehung eines steuerlichen Beraters.',
        ]
      };
    },

    tooltip() {
      return {
        marital:      'Single: Wählen Sie „Single“, wenn Sie ledig, alleinstehend oder vom Finanzamt einzeln veranlagt werden.<br>Ehe-/Lebenspartner: Wählen Sie „Ehe-/Lebenspartner“, wenn Sie einen Ehe-/Lebenspartner haben und vom Finanzamt zusammen veranlagt werden.',
        birth_year:   'Ihr Geburtsjahr wird für die Berechnung der Förderung bis zum geplanten Rentenbeginn benötigt.',
        child_num:    'Für jedes Kind, für das Sie Kindergeld erhalten, bekommen Sie eine jährliche staatliche Zulage gutgeschrieben - bis zum 25. Geburtstag.' ,
        child_birth1: 'Für alle ab dem 1. Januar 2008 geborenen Kinder erhalten Sie eine jährliche Kinderzulage von 300 Euro. Für davor geborene Kinder erhalten Sie 185 Euro.',
        gross_salary: 'Für den gewählten Familienstand "Ehe-/Lebenspartner" geben Sie bitte das Familieneinkommen an. <br>Um die vollen Zulagen zu erhalten, ist ein jährlicher Mindesteigenbeitrag erforderlich. Dieser beträgt 4 Prozent des Vorjahresbruttoeinkommens (bei Beamten 4 Prozent der Bezüge des Vorjahres und bei Landwirten 4 Prozent der landwirtschaftlichen Einkünfte des Vorvorjahres), maximal ' + formatMoney(this.rr_config.hoechstbetrag_single, 2) + ' Euro (bzw. 2.160,00 Euro bei Zusammenveranlagung). Wird ein geringerer Eigenbeitrag eingezahlt, wird die Zulage anteilig gekürzt. Bei geringem Einkommen im Vorjahr, muss mindestens der Sockelbeitrag von ' + formatMoney(this.rr_config.max_foerder_single) + ' Euro im Jahr in den Vertrag einbezahlt werden. Der Mindestbeitrag bei der RiesterRente FlexVario beträgt ' + formatMoney(this.rr_config.max_foerder_spouses) + ' Euro im Jahr.',
        retire_age:   'Sie können Ihren Rentenbeginn individuell zwischen dem 62. und 67. Geburtstag wählen.',
        eigenbeitrag: 'Bei diesem Betrag handelt es sich um den Mindesteigenbeitrag für eine optimale steuerliche Förderung, basierend auf Ihrem angegebenen Bruttojahreseinkommen.\n\nDer Eigenbeitrag ergibt sich aus dem Zahlbetrag abzüglich der staatlichen Förderung. \n\nSelbstverständlich können Sie auch einen individuellen, auf Ihre finanziellen Möglichkeiten zugeschnittenen Betrag in Ihrem Beratungsgespräch vereinbaren. Liegt dieser unter dem Mindesteigenbeitrag, fällt die steuerliche Förderung entsprechend geringer aus.'
      };
    },

    errorMessages() {
      return {
        marital: {
          valid: 'Bitte wählen Sie Ihren Familienstand.'
        },
        birth_year: {
          valid: `Bitte wählen Sie ein Geburtsjahr zwischen ${ this.minBirthYear } und ${ this.maxBirthYear }`
        },
        child_num: {
          valid: 'Bitte wählen Sie die Anzahl Ihrer Kinder.'
        },
        child_birth1: {
          valid: `Bitte wählen Sie ein Geburtsjahr ab ${ this.minChildYear }.`
        },
        child_birth2: {
          valid: `Bitte wählen Sie ein Geburtsjahr ab ${ this.minChildYear }.`
        },
        child_birth3: {
          valid: `Bitte wählen Sie ein Geburtsjahr ab ${ this.minChildYear }.`
        },
        child_birth4: {
          valid: `Bitte wählen Sie ein Geburtsjahr ab ${ this.minChildYear }.`
        },
        gross_salary: {
          required: 'Bitte geben Sie Ihr Bruttojahreseinkommen ein.',
          valid: this.config.theme === 'vkb'
            ? `Bei einem Einkommen unter dem aktuellen Grundfreibetrag von derzeit ${this.rr_config.grund_frei_betrag_single} € (Singles) und ${this.rr_config.grund_frei_betrag_spouses} (Ehe-/Lebenspartner) wenden Sie sich zur individuellen Berechnung bitte an einen <a href="https://www.vkb.de/content/_resources/beratersuche/" target="_blank">Berater in Ihrer Nähe.</a>`
            : `Bei einem Einkommen unter dem aktuellen Grundfreibetrag von derzeit ${this.rr_config.grund_frei_betrag_single} € (Singles) und ${this.rr_config.grund_frei_betrag_spouses} (Ehe-/Lebenspartner) wenden Sie sich zur individuellen Berechnung bitte an einen <a href="https://www.feuersozietaet.de/content/_resources/beratersuche/" target="_blank">Berater in Ihrer Nähe.</a>`
        },
        retire_age: {
          valid: 'Bitte wählen Sie Ihren geplanten Rentenbeginn.'
        },
      };
    }
  },

  mounted() {
    this.loadConfig();
    this.triggerTransitionEvent('schnellrechner', 1, 'persoenlicheSituation', 1, 'persoenlicheSituation');
  },

  methods: {
    checkPDF () {
      this.apiRequest
        .get(`${this.config.paths.taskData}${this.pdfJobId}/`, {
          params: { anti_cache: randomStr() }
        })
        .then(response => {
          if (response && response.data) {
            if (response.data.status === 1) {
              throw new Error('Not finished');
            }
            const pdfData = response.data.status_data.data;
            const fileName = this.getPDFName('Riester_Rente.pdf');
            downloadBase64(pdfData, fileName);
            this.pdfPollTimer = null;
            this.pdfJobId = null;
          }
        })
        .catch(() => {
          this.pdfPollTimer = setTimeout(this.checkPDF, POLL_INTERVAL);
        });
    },

    changeChildren() {
      if (this.form.child_num < 4) {
        this.form.child_birth4 = '';
      }

      if (this.form.child_num < 3) {
        this.form.child_birth3 = '';
      }

      if (this.form.child_num < 2) {
        this.form.child_birth2 = '';
      }

      if (this.form.child_num < 1) {
        this.form.child_birth1 = '';
      }
    },

    getErrorMessage(field = null) {
      const validity = field
        ? this.$v.form[field]
        : this.$v.form;

      if (!validity || !validity.$dirty) {
        return;
      }

      const error_key = Object.keys(validity.$params).find(k => validity[k] === false);

      return error_key
        ? this.errorMessages[field][error_key]
        : '';
    },

    loadConfig() {
      if (!this.apiRequest) {
        return;
      }
      this.apiRequest
        .get(this.config.paths.config, { params: { year: 0, customer: '' } })
        .then(response => {
          if (response && response.data) {
            this.rr_config = response.data;
          }
        });
    },

    loadResult() {
      if (!this.apiRequest) {
        return;
      }

      const _params = {
        year: this.rr_config.Year,
        customer: '',
        familienstand: this.form.marital,
        geburtsjahr: this.form.birth_year,
        geburtsjahr_k1: this.form.child_birth1,
        geburtsjahr_k2: this.form.child_birth2,
        geburtsjahr_k3: this.form.child_birth3,
        geburtsjahr_k4: this.form.child_birth4,
        jahresbruttogehalt: this.form.gross_salary,
        rentenbeginn: this.form.retire_age
      };

      this.apiRequest
        .get(this.config.paths.calc, { params: _params })
        .then(response => {
          if (response && response.data) {
            this.result = response.data;
            Object.keys(this.result)
              .map(key => this.result[key] = this.result[key] > 0 ? this.result[key] : 0);

            setTimeout(() => {
              this.scrollToElement('result', 0);
            }, 300);
          }
        });
    },

    onBerechnenClick(e, bReset = false) {
      e.preventDefault();

      this.triggerValidationEvent(
        this.$v.form,
        Object.keys(this.form),
        this.errorMessages,
        this.fieldNames
      );

      if (bReset) {
        this.checkValid = false;
        this.resetForm();
        return false;
      }

      this.checkValid = true;
      this.$v.$touch();

      if (this.$v.$invalid) {
        return false;
      }

      this.checkValid = false;
      this.loadResult();
    },

    onContactClick(e) {
      e.preventDefault();

      this.triggerActionEvent('zurBeratung (submit)');

      if (this.config.contact_url) {
        window.open(this.config.contact_url, '_top');
        return false;
      }

      window.open('https://www.vkb.de/content/versicherungen/rente/gefoerderte-rente/riester-rente/beraten-anfragen/', '_top');
    },

    onGetPdfClick(e) {
      e.preventDefault();

      this.triggerActionEvent('berechnungSpeichern (submit)');

      if (this.$v.$invalid || this.pdfJobId) {
        return false;
      }

      if (!this.result) {
        return false;
      }

      const _pdfData = {
        anti_cache: randomStr(),
        data: JSON.stringify(Object.assign({
          result: this.result,
          chartData: this.chartData,
          year: this.currentYear
        }, this.form)),
        preview: false,
        template_name: 'riester',
        theme: this.getPDFTheme()
      };

      this.apiRequest
        .get(this.config.paths.pdf, { params: _pdfData })
        .then(response => {
          if (response && response.data && response.data.id) {
            this.pdfJobId = response.data.id;
            this.pdfPollTimer = setTimeout(this.checkPDF, POLL_INTERVAL);
          }
        })
        .catch(() => {
          this.pdfJobId = null;
        });
    },

    onInputBirthYear() {
      this.form.retire_age = 0;
    },

    onSelectMarital() {
      this.form.gross_salary = '';
    },

    resetForm() {
      Object.keys(this.form).map(_key => {
        this.form[_key] = '';
      });
      this.result = null;
      this.$v.$reset();
    },

    formatMoney,
    formatPercent
  },
  watch: {
    form: {
      deep: true,
      handler() {
        this.result = null;
      }
    },
  },

  validations: {
    form: {
      marital: {
        valid(value) {
          return value > 0;
        }
      },
      birth_year: {
        valid(value) {
          return value >= this.minBirthYear && value <= this.maxBirthYear;
        }
      },
      child_num: {
        valid(value) {
          return value >= 0;
        }
      },
      child_birth1: {
        valid(value) {
          return this.form.child_num < 1 || (value >= this.minChildYear && value <= this.currentYear);
        }
      },
      child_birth2: {
        valid(value) {
          return this.form.child_num < 2 || (value >= this.minChildYear && value <= this.currentYear);
        }
      },
      child_birth3: {
        valid(value) {
          return this.form.child_num < 3 || (value >= this.minChildYear && value <= this.currentYear);
        }
      },
      child_birth4: {
        valid(value) {
          return this.form.child_num < 4 || (value >= this.minChildYear && value <= this.currentYear);
        }
      },
      gross_salary: {
        required(value) {
          return value !== null && value !== '';
        },
        valid(value) {
          if (value === null || value === '') {
            return true;
          }

          const _value = parseInt(value);
          if (isNaN(_value)) {
            return false;
          }
          return this.form.marital === 1
                 ? value > this.rr_config.grund_frei_betrag_single && value <= this.rr_config.max_income
                 : this.form.marital === 2
                   ? value > this.rr_config.grund_frei_betrag_spouses && value <= this.rr_config.max_income
                   : value >= 0 && value <= this.rr_config.max_income;
        }
      },
      retire_age: {
        valid(value) {
          return value > 0;
        }
      },
    }
  }
};



// WEBPACK FOOTER //
// ./src/apps/riester_rente/logic.js